import { MIN_LENGTH, SCORE_LABELS, usePasswordMeter } from '../lib/passwordMeter';
import { Field } from './ui';

/**
 * Password input with a strength meter.
 *
 * The bar is coloured *and* labelled — "Fair" is readable when the colour is
 * not (§9, WCAG 1.4.1). Scoring happens server-side; see `passwordMeter.ts`
 * for why.
 */
export default function PasswordField({
  label = 'Password',
  value,
  onChange,
  email,
  error,
  autoComplete = 'new-password',
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  email?: string;
  error?: string;
  autoComplete?: string;
}) {
  const meter = usePasswordMeter(value, email);
  const tooShort = value.length > 0 && value.length < MIN_LENGTH;
  const strongEnough = meter.ready && meter.acceptable;

  const barColour = !meter.ready
    ? 'bg-black/20 dark:bg-white/20'
    : meter.score <= 1
      ? 'bg-bad'
      : meter.score === 2
        ? 'bg-warn'
        : 'bg-good';

  return (
    <div>
      <Field
        label={label}
        error={error}
        hint={`At least ${MIN_LENGTH} characters. A few unrelated words beats a short password with symbols.`}
      >
        {(props) => (
          <input
            {...props}
            className="input"
            type="password"
            name="password"
            autoComplete={autoComplete}
            required
            minLength={MIN_LENGTH}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
      </Field>

      {value.length > 0 && (
        <div className="mt-2">
          <div
            className="h-1.5 w-full overflow-hidden rounded-full bg-black/10 dark:bg-white/10"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={4}
            aria-valuenow={meter.ready ? meter.score : 0}
            aria-label="Password strength"
          >
            <div
              className={`h-full transition-all ${barColour}`}
              style={{ width: `${meter.ready ? ((meter.score + 1) / 5) * 100 : 8}%` }}
            />
          </div>

          <p
            className={`mt-1 text-xs ${strongEnough ? 'text-good' : 'text-ink-muted dark:text-ink-inverse-muted'}`}
            aria-live="polite"
          >
            {tooShort
              ? `${MIN_LENGTH - value.length} more character${MIN_LENGTH - value.length === 1 ? '' : 's'} needed.`
              : meter.unavailable
                ? 'Strength check unavailable — it will still be checked when you submit.'
                : meter.ready
                  ? `${SCORE_LABELS[meter.score]}${strongEnough ? ' — good to go.' : '. Needs to be at least Strong.'}`
                  : 'Checking…'}
          </p>

          {meter.ready && !strongEnough && (meter.warning || meter.suggestions[0]) && (
            <p className="mt-0.5 text-xs text-warn">{meter.warning || meter.suggestions[0]}</p>
          )}
        </div>
      )}
    </div>
  );
}
